/* ==========================================================================
   Safegear - Custom size block (sections/sg-custom-size.liquid). 2026.10.08

   Reads the JSON the section renders, and:
     - limits each dimension to the series' smallest .. largest value
     - picks the PRICE BASIS: the cheapest series size the custom size fits
       into (H, W and D all <= that size); if none fits, the size it goes
       beyond in the fewest dimensions, then the cheapest
     - prices it in the variant whose options best match the selected one
       (same lock, same colour where it exists), + rate % per supplement.
       NUMBER OF SUPPLEMENTS (owner 2026.10.08, two corrections):
         = min(dimensions that differ from the basis,
               dimensions the customer changed from this product's size)
       Smaller counts as well as larger. Examples:
         Gemini Pro 1 -> 1900 x 1200 x 430: basis Gemini Pro 80, only the
           depth differs from it -> 1 supplement.
         Gemini Pro 40 -> width 700: basis Gemini Pro 60 (3 differ), but the
           customer changed 1 -> 1 supplement.
         Gemini Pro 40 -> exactly 800 x 600 x 500 = Gemini Pro 20 -> none.
     - draws the 3D figure (cabinet projection, 45 degrees, half depth -
       the same projection as the depth line on the size drawing)
     - fills the request form's hidden fields
   Follows the buy column's variant: Horizon's variant:update event, the
   custom picker's shopify:product:select, and ?variant= in the URL.
   ========================================================================== */
(() => {
  'use strict';

  const KEYS = ['h', 'w', 'd'];
  const BLUE = '#4b74b5';

  /* Money in the storefront's own format: the section renders 1234567.89
     with the money filter and we reuse its symbols and separators. */
  function moneyFormatter(sample) {
    let m = /^(.*?)1(\D?)234(\D?)567(\D)89(.*)$/s.exec(sample);
    if (m) {
      const [, pre, th, , dec, post] = m;
      return (cents) => {
        const neg = cents < 0 ? '-' : '';
        const v = Math.abs(Math.round(cents));
        const whole = String(Math.floor(v / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, th);
        return neg + pre + whole + dec + String(v % 100).padStart(2, '0') + post;
      };
    }
    m = /^(.*?)1(\D?)234(\D?)568(.*)$/s.exec(sample);
    if (m) {
      const [, pre, th, , post] = m;
      return (cents) => {
        const whole = String(Math.round(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, th);
        return pre + whole + post;
      };
    }
    return (cents) => (cents / 100).toFixed(2);
  }

  function init(root) {
    if (root.dataset.sgcsReady) return;
    root.dataset.sgcsReady = '1';
    let D;
    try {
      D = JSON.parse(root.querySelector('[data-sgcs-data]').textContent);
    } catch (e) {
      return;
    }
    /* SAFEGEAR 2026.10.13: call-for-price sizes arrive with "price": null (the hidden
       price is no longer printed in the page). Treat them as unpriced so they are
       only used as the basis when nothing priced fits; such a basis shows no price. */
    const unpriced = (vs) => (vs || []).forEach((v) => { if (v.price == null) v.price = Infinity; });
    unpriced(D.current && D.current.variants);
    (D.sizes || []).forEach((s) => unpriced(s.variants));
    const money = moneyFormatter(D.money || '');
    const cur = D.current;
    const sizes = D.sizes.slice();
    if (!sizes.some((s) => s.id === cur.id)) {
      sizes.push({ id: cur.id, name: cur.name, h: cur.h, w: cur.w, d: cur.d, cfp: D.cfp, variants: cur.variants });
    }
    const MIN = {}, MAX = {};
    KEYS.forEach((k) => {
      MIN[k] = Math.min(...sizes.map((s) => s[k]));
      MAX[k] = Math.max(...sizes.map((s) => s[k]));
    });
    const STD = { h: cur.h, w: cur.w, d: cur.d };
    const S = { ...STD };
    let selectedId = cur.selected;
    const rate = (Number(D.rate) || 0) / 100;
    const label = { h: D.t.height, w: D.t.width, d: D.t.depth };

    const $ = (sel) => root.querySelector(sel);
    const dims = {};
    root.querySelectorAll('[data-sgcs-dim]').forEach((row) => {
      const k = row.dataset.sgcsDim;
      const num = row.querySelector('.sgcs__input');
      const rng = row.querySelector('.sgcs__range');
      [num, rng].forEach((i) => { i.min = MIN[k]; i.max = MAX[k]; });
      row.querySelector('[data-sgcs-min]').textContent = String(MIN[k]);
      row.querySelector('[data-sgcs-max]').textContent = String(MAX[k]);
      dims[k] = { row, num, rng };
      rng.addEventListener('input', () => { S[k] = +rng.value; render(); });
      num.addEventListener('change', () => {
        let v = Math.round(+num.value / 5) * 5;
        if (!v) v = STD[k];
        S[k] = Math.max(MIN[k], Math.min(MAX[k], v));
        render();
      });
      num.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); num.dispatchEvent(new Event('change')); } });
    });

    /* ---- price basis ---- */
    const over = (s) => KEYS.filter((k) => S[k] > s[k]).length;
    function selectedOptions() {
      const v = cur.variants.find((x) => x.id === selectedId) || cur.variants[0];
      return v ? v.o : [];
    }
    function variantFor(size) {
      const want = selectedOptions();
      let best = null, bestScore = -1;
      size.variants.forEach((v) => {
        const score = v.o.filter((val) => want.includes(val)).length;
        if (score > bestScore || (score === bestScore && v.price < best.price)) { best = v; bestScore = score; }
      });
      return best;
    }
    function basis() {
      const fits = sizes.filter((s) => over(s) === 0);
      const least = Math.min(...sizes.map(over));
      const pool = fits.length ? fits : sizes.filter((s) => over(s) === least);
      let best = null;
      pool.forEach((s) => {
        const v = variantFor(s);
        const p = v ? v.price : Infinity;
        if (!best || p < best.price) best = { size: s, variant: v, price: p };
      });
      return best;
    }

    /* ---- 3D drawing ---- */
    const svg = $('[data-sgcs-svg]');
    function draw(B) {
      const W = 346, H = 392, L = 46, F = 372, k = Math.SQRT1_2 / 2;
      // The scale labels match the size comparison exactly (.sg-cmp__grid in
      // assets/sg-size.css: 10px, #767676, "N cm" on every line). The SVG is
      // scaled to its box, so the font size is converted from CSS px to SVG
      // units with the rendered height; redrawn on resize.
      const shown = svg.getBoundingClientRect().height;
      const unit = shown > 0 ? H / shown : 1;
      const fs = (px) => (px * unit).toFixed(2);
      const sc = Math.min((F - 14) / (MAX.h + MAX.d * k), (W - L - 14) / (MAX.w + MAX.d * k));
      const pt = (X, Y, Z) => [L + (X + Z * k) * sc, F - (Y + Z * k) * sc];
      const poly = (pts, a) => `<polygon points="${pts.map((p) => p.map((n) => n.toFixed(1)).join(',')).join(' ')}" ${a}/>`;
      const box = (w, h, d, c, op, extra) => {
        const front = [pt(0, 0, 0), pt(w, 0, 0), pt(w, h, 0), pt(0, h, 0)];
        const top = [pt(0, h, 0), pt(w, h, 0), pt(w, h, d), pt(0, h, d)];
        const side = [pt(w, 0, 0), pt(w, 0, d), pt(w, h, d), pt(w, h, 0)];
        const e = `stroke="${c.edge}" stroke-width="1.2" stroke-linejoin="round" ${extra || ''}`;
        return poly(side, `fill="${c.side}" fill-opacity="${op}" ${e}`) + poly(top, `fill="${c.top}" fill-opacity="${op}" ${e}`) + poly(front, `fill="${c.front}" fill-opacity="${op}" ${e}`);
      };
      const none = (edge) => ({ front: 'none', top: 'none', side: 'none', edge });
      let g = '';
      // Same gridline steps as sections/sg-size-comparison.liquid.
      const step = MAX.h > 6000 ? 1000 : MAX.h > 2500 ? 500 : MAX.h > 1200 ? 200 : 100;
      for (let c = step; c <= MAX.h; c += step) {
        const y = pt(0, c, 0)[1];
        g += `<text x="${L - 6}" y="${y.toFixed(1)}" text-anchor="end" dominant-baseline="middle" font-size="${fs(10)}" fill="#767676">${c / 10} cm</text><line x1="${L - 3}" y1="${y.toFixed(1)}" x2="${L}" y2="${y.toFixed(1)}" stroke="#c9c9c9"/>`;
      }
      g += poly([pt(0, 0, 0), pt(MAX.w, 0, 0), pt(MAX.w, 0, MAX.d), pt(0, 0, MAX.d)], `fill="${BLUE}" fill-opacity=".06" stroke="none"`);
      g += box(MAX.w, MAX.h, MAX.d, { front: BLUE, top: BLUE, side: BLUE, edge: BLUE }, 0.07, 'stroke-width="1.5" stroke-dasharray="5 4"');
      g += box(S.w, S.h, S.d, { front: '#38761d', top: '#5e9a42', side: '#2b5c16', edge: '#24501a' }, 1);
      const hp = pt(S.w * 0.12, S.h * 0.55, 0);
      g += `<rect x="${(hp[0] - 2).toFixed(1)}" y="${(hp[1] - 14).toFixed(1)}" width="4" height="28" rx="2" fill="#1a1a1a"/>`;
      g += box(MIN.w, MIN.h, MIN.d, none('#767676'), 1, 'stroke-width="1.5" stroke-dasharray="5 4"');
      if (B && KEYS.some((kk) => S[kk] !== B.size[kk])) g += box(B.size.w, B.size.h, B.size.d, none('#8c8c8c'), 1, 'stroke-width="1.5"');
      g += `<rect x="${L}" y="${F}" width="${W - L - 10}" height="4" fill="${BLUE}"/>`;
      const a = pt(MAX.w / 2, MAX.h, MAX.d), y0 = pt(S.w / 2, 0, 0);
      g += `<g font-size="13" font-weight="700" fill="#767676"><text x="${a[0].toFixed(1)}" y="${(a[1] - 5).toFixed(1)}" text-anchor="middle">${MAX.w} × ${MAX.h} × ${MAX.d}</text><text x="${y0[0].toFixed(1)}" y="${F + 16}" text-anchor="middle" fill="#38761d">${S.w} × ${S.h} × ${S.d}</text></g>`;
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      svg.innerHTML = g;
    }

    /* ---- render ---- */
    let last = null;
    function render() {
      const B = basis();
      const diffB = KEYS.filter((k) => S[k] !== B.size[k]);
      const diffP = KEYS.filter((k) => S[k] !== STD[k]);
      const n = Math.min(diffB.length, diffP.length);
      // Which dimensions carry the supplement: those the customer changed
      // that also differ from the basis first, then the rest of diffB.
      const changed = diffB.filter((k) => diffP.includes(k))
        .concat(diffB.filter((k) => !diffP.includes(k)))
        .slice(0, n);
      // Supplement rounded to whole currency units - it is an estimate.
      const supl = Math.round((B.price * rate * changed.length) / 100) * 100;
      const total = B.price + supl;
      const priced = isFinite(B.price) && !B.size.cfp && !D.cfp;
      last = { B, changed, supl, total, priced };

      KEYS.forEach((k) => {
        const d = dims[k];
        d.row.classList.toggle('sgcs__dim--changed', changed.includes(k));
        if (document.activeElement !== d.num) d.num.value = S[k];
        if (document.activeElement !== d.rng) d.rng.value = S[k];
      });

      const set = (sel, txt) => { const el = $(sel); if (el) el.textContent = txt; };
      const priceBox = $('.sgcs__price');
      if (priceBox) priceBox.hidden = !priced;
      if (priced) {
        set('[data-sgcs-bname]', B.size.name);
        set('[data-sgcs-bdim]', `${B.size.h} × ${B.size.w} × ${B.size.d} mm`);
        set('[data-sgcs-bprice]', money(B.price));
        set('[data-sgcs-supl-l]', changed.length
          ? D.t.supplement.replace('[n]', changed.length).replace('[rate]', D.rate) + ` (${changed.map((k) => label[k].toLowerCase()).join(', ')})`
          : D.t.none);
        set('[data-sgcs-supl]', money(supl));
        set('[data-sgcs-total]', money(total));
        // Tax-inclusive markets: "incl. VAT · net excl. VAT" under the total,
        // like the net/gross pair in snippets/custom.price-vat.liquid.
        const netRow = $('[data-sgcs-net-row]');
        if (netRow) {
          netRow.hidden = !(D.vat > 0);
          if (D.vat > 0) set('[data-sgcs-net]', `${D.t.inVat} · ${money(Math.round(total / (1 + D.vat)))} ${D.t.exVat}`);
        }
      }
      draw(B);
    }
    if (window.ResizeObserver) {
      let w0 = 0;
      new ResizeObserver(() => {
        const w = Math.round(svg.getBoundingClientRect().width);
        if (w && w !== w0 && last) { w0 = w; draw(last.B); }
      }).observe(svg);
    }

    $('[data-sgcs-reset]').addEventListener('click', () => { Object.assign(S, STD); render(); });

    /* ---- request dialog ---- */
    const dlg = $('[data-sgcs-dlg]');
    const fill = () => {
      const { B, changed, supl, total, priced } = last;
      const v = cur.variants.find((x) => x.id === selectedId) || cur.variants[0] || {};
      const dim = (o) => `${o.h} × ${o.w} × ${o.d} mm`;
      const pct = Math.round(rate * 100 * changed.length);
      const f = {
        product: `${cur.title}${v.title && v.title !== 'Default Title' ? ' – ' + v.title : ''}`,
        sku: v.sku || '',
        std: `${dim(STD)} (H × W × D)`,
        custom: dim(S),
        basis: `${B.size.name} (${dim(B.size)})${priced ? ', ' + money(B.price) : ''}`,
        changed: changed.map((k) => label[k]).join(', ') || '–',
        supplement: `${pct}%${priced ? ' = ' + money(supl) : ''}`,
        estimate: priced ? money(total) : '–',
      };
      root.querySelectorAll('[data-sgcs-f]').forEach((i) => { i.value = f[i.dataset.sgcsF] || ''; });
      const sum = $('[data-sgcs-sum]');
      if (sum) {
        sum.innerHTML = '';
        const lines = [
          [cur.name, true], [f.product.replace(cur.title, '').replace(/^ – /, ''), false],
          [`${label.h} × ${label.w} × ${label.d}: ${f.custom}`, true],
          [`${D.t.basis}: ${f.basis}`, false],
        ];
        if (priced) lines.push([`${D.t.supplement.replace('[n]', changed.length).replace('[rate]', D.rate)}: ${money(supl)}`, false]);
        if (priced) lines.push([`${D.t.estimate}: ${money(total)}`, false]);
        lines.forEach(([t, b]) => {
          if (!t) return;
          const el = document.createElement(b ? 'b' : 'span');
          el.textContent = t;
          sum.appendChild(el);
          sum.appendChild(document.createElement('br'));
        });
      }
    };
    $('[data-sgcs-open]').addEventListener('click', () => {
      fill();
      if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
    });
    dlg.querySelector('[data-sgcs-close]').addEventListener('click', () => dlg.close());
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    const form = dlg.querySelector('form');
    if (form) form.addEventListener('submit', fill);

    /* After a submit Shopify reloads the page with the result: show it. */
    if (dlg.querySelector('[data-sgcs-posted]') && dlg.showModal) dlg.showModal();

    /* ---- follow the variant picker ---- */
    const onVariant = (id) => {
      id = Number(id);
      if (id && id !== selectedId && cur.variants.some((x) => x.id === id)) { selectedId = id; render(); }
    };
    document.addEventListener('variant:update', (e) => {
      const r = e.detail && e.detail.resource;
      if (r && r.id) onVariant(r.id);
    });
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (t && t.name === 'id' && t.closest('form[action*="/cart/add"]')) onVariant(t.value);
    });
    /* Checked on the live page 2026.10.08: Safegear's custom variant picker
       fires shopify:product:select and rewrites ?variant= with replaceState,
       but no variant:update. So also read the selected variant back from the
       URL and the cart form's id field - after that event, and on a light
       poll, which catches any other route that changes the variant. */
    const readBack = () => {
      const fromUrl = new URLSearchParams(location.search).get('variant');
      const idField = document.querySelector('form[action*="/cart/add"] [name="id"]');
      onVariant(fromUrl || (idField && idField.value));
    };
    document.addEventListener('shopify:product:select', () => setTimeout(readBack, 50));
    setInterval(readBack, 700);

    render();
  }

  const boot = () => document.querySelectorAll('[data-sgcs]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
